import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonContent
} from '@ionic/react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Check, ChevronLeft, ChevronRight, CreditCard, Eye, FileWarning, Gauge, LifeBuoy, Loader2, LogOut, Pencil, Plus, RefreshCw, Search, Shield, Sparkles, Star, Trash2, UserPlus, Users, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BirthDateInput } from '../components/ui/birth-date-input';
import { TimeInput } from '../components/ui/time-input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { AdminCreateSubscriptionDto, AdminUserListItemDto, AdminUserUpdateDto, SubscriptionInterval, SubscriptionPlanListItemDto, SubscriptionTier, UserRole, UserStatus, UserSubscription, UserSubscriptionStatus, UserTreatmentPreference } from '../api';
import { useAuth } from 'react-oidc-context';
import { useSubscription } from '../SubscriptionProvider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTranslation } from 'react-i18next';
import { buildUtcDatePreservingClock } from '../helpers/time';
import {
  type BirthDateDraft,
  EMPTY_BIRTH_DATE_DRAFT,
  formatCivilBirthDate,
  isEmptyBirthDateDraft,
  maxBirthDate,
  parseBirthDate,
  parseBirthTime,
  toBirthDateDraft,
} from '../helpers/birthDateTime';
import {
  getAdminSubscriptionRefundRequests,
  updateAdminSubscriptionRefundRequest,
  type AdminSubscriptionRefundRequest,
  type SubscriptionRefundRequestStatus,
} from '../features/subscriptionRefundRequests';
import { searchBirthPlaceSuggestions, type BirthPlaceSuggestion } from '../features/birthPlaceSearch';

const LANGUAGE_OPTIONS = [
  { value: 'pt', label: 'Português' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
] as const;

interface User {
  id: string;
  authId: string;
  name: string;
  email: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  birthLatitude: number | undefined;
  birthLongitude: number | undefined;
  language: string;
  treatmentPreference: UserTreatmentPreference;
  role: "user" | "admin";
  createdAt: string;
  status: "active" | "inactive";
  includedCredits?: number;
  usedCredits?: number;
  remainingCredits?: number;
  creditCycleEndsAtUtc?: string;
}

interface UserFormData {
  name: string;
  email: string;
  birthDate: BirthDateDraft;
  birthTime: string;
  birthPlace: string;
  birthLatitude: number | undefined;
  birthLongitude: number | undefined;
  language: string;
  treatmentPreference: UserTreatmentPreference;
  role: "user" | "admin";
}

type UserRoleFilter = "all" | User["role"]
type UserStatusFilter = "all" | User["status"]
type UserRegistrationDateFilter = "all" | "last7Days" | "last30Days" | "custom"
type SubscriptionStatusFilter = "all" | UserSubscriptionStatus.Active | UserSubscriptionStatus.Canceled
type SubscriptionTierFilter = "all" | SubscriptionTier.Basic | SubscriptionTier.Pro

type LocationSuggestion = BirthPlaceSuggestion

type AiReadingJobStatus = "Pending" | "Processing" | "Completed" | "Failed" | "Canceled"

interface AiReadingJob {
  id: string
  natalChartId: string
  context: string
  profileKey: string | null
  date: string | null
  startDay: string | null
  endDay: string | null
  language: string
  treatmentPreference: UserTreatmentPreference
  schemaVersion: string
  status: AiReadingJobStatus
  batchId: string | null
  error: string | null
  attempts: number
  createdAtUtc: string
  updatedAtUtc: string | null
}

interface AdminAiBudgetContextSummary {
  context?: string | null
  planTier?: SubscriptionTier | null
  requests?: number
  estimatedTokens?: number
  estimatedCostUsd?: number
}

interface AdminAiBudgetUserSummary {
  userIdHash?: string | null
  planTier?: SubscriptionTier | null
  requests?: number
  estimatedTokens?: number
  estimatedCostUsd?: number
  dailyBudgetUsd?: number
  hasDailyBudget?: boolean
  isOverBudget?: boolean
}

interface AdminAiBudgetSnapshot {
  date?: Date | string | null
  totalRequests?: number
  basicRequests?: number
  hasBasicRequests?: boolean
  estimatedCostUsd?: number
  dailyBudgetUsd?: number
  isOverBudget?: boolean
  overBudgetUsers?: number
  contexts?: AdminAiBudgetContextSummary[] | null
  users?: AdminAiBudgetUserSummary[] | null
}

type AdminAiBudgetApiClient = {
  apiAdminAiBudgetGet?: (date: Date | undefined) => Promise<AdminAiBudgetSnapshot>
  baseUrl?: string
  http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }
}

const Admin: React.FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { refresh: refreshSubscription } = useSubscription();
  const api = useAstroApiClient();

  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    api
      .apiAdminUsersGet()
      .then(dtos => {
        const mapped: User[] = dtos.map((u: AdminUserListItemDto) => {
          const creditCycleEndsAt = u.creditCycleEndsAtUtc
            ? new Date(u.creditCycleEndsAtUtc)
            : null
          return {
            id: u.id ?? '',
            authId: u.authId ?? '',
            name: u.name ?? '',
            email: u.email ?? '',
            // GR-027: civil values of the birth place, never split from the UTC instant.
            birthDate: u.birthLocalDate ?? '',
            birthTime: u.birthLocalDate ? u.birthLocalTime ?? '' : '',
            birthPlace: u.birthPlace ?? '',
            birthLatitude: u.birthLatitude,
            birthLongitude: u.birthLongitude,
            language: u.language ?? 'pt',
            treatmentPreference: u.treatmentPreference ?? UserTreatmentPreference.Masculine,
            role: u.role === UserRole.Admin ? 'admin' : 'user',
            createdAt: u.createdAt ? u.createdAt.toISOString() : '',
            status: u.status === UserStatus.Active ? 'active' : 'inactive',
            includedCredits: typeof u.includedCredits === 'number' ? u.includedCredits : undefined,
            usedCredits: typeof u.usedCredits === 'number' ? u.usedCredits : undefined,
            remainingCredits: typeof u.remainingCredits === 'number' ? u.remainingCredits : undefined,
            creditCycleEndsAtUtc: creditCycleEndsAt && !Number.isNaN(creditCycleEndsAt.getTime())
              ? creditCycleEndsAt.toISOString()
              : undefined,
          };
        });

        setUsers(mapped);
      })
      .catch(err => {
        console.error('Erro ao carregar utilizadores admin:', err);
      });
  }, [api, auth.isAuthenticated]);

  const [searchTerm, setSearchTerm] = useState("")
  const [userRoleFilter, setUserRoleFilter] = useState<UserRoleFilter>("all")
  const [userStatusFilter, setUserStatusFilter] = useState<UserStatusFilter>("all")
  const [userRegistrationDateFilter, setUserRegistrationDateFilter] = useState<UserRegistrationDateFilter>("all")
  const [userRegistrationStartDate, setUserRegistrationStartDate] = useState("")
  const [userRegistrationEndDate, setUserRegistrationEndDate] = useState("")
  const [subscriptionSearchTerm, setSubscriptionSearchTerm] = useState("")
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<SubscriptionStatusFilter>(UserSubscriptionStatus.Active)
  const [subscriptionTierFilter, setSubscriptionTierFilter] = useState<SubscriptionTierFilter>(SubscriptionTier.Pro)
  const [isCreateSubscriptionDialogOpen, setIsCreateSubscriptionDialogOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isViewSubscriptionDialogOpen, setIsViewSubscriptionDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isTerminateSessionsDialogOpen, setIsTerminateSessionsDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isCancelSubscriptionDialogOpen, setIsCancelSubscriptionDialogOpen] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState<UserSubscription | null>(null)
  const [formData, setFormData] = useState<UserFormData>({
    name: "",
    email: "",
    birthDate: EMPTY_BIRTH_DATE_DRAFT,
    birthTime: "",
    birthPlace: "",
    birthLatitude: undefined,
    birthLongitude: undefined,
    language: "pt",
    treatmentPreference: UserTreatmentPreference.Masculine,
    role: "user",
  })

  const [showBirthErrors, setShowBirthErrors] = useState(false)

  const [subscriptionFormData, setSubscriptionFormData] = useState({
    userId: "",
    planId: "",
    status: UserSubscriptionStatus.Active,
    endsAtUtc: "",
  })

  const [birthPlaceQuery, setBirthPlaceQuery] = useState("")
  const [birthPlaceSuggestions, setBirthPlaceSuggestions] = useState<LocationSuggestion[]>([])
  const [isSearchingBirthPlace, setIsSearchingBirthPlace] = useState(false)
  const [birthPlaceError, setBirthPlaceError] = useState<string | null>(null)

  const [usersPerPage, setUsersPerPage] = useState(10)
  const [usersPage, setUsersPage] = useState(1)
  const [subsPerPage, setSubsPerPage] = useState(10)
  const [subsPage, setSubsPage] = useState(1)
  const [createSubUserSearch, setCreateSubUserSearch] = useState("")
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)
  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([])
  const [subscriptionsTotalCount, setSubscriptionsTotalCount] = useState(0)
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlanListItemDto[]>([])
  const [refundRequestSearchTerm, setRefundRequestSearchTerm] = useState("")
  const [refundRequestsPerPage, setRefundRequestsPerPage] = useState(10)
  const [refundRequestsPage, setRefundRequestsPage] = useState(1)
  const [refundRequests, setRefundRequests] = useState<AdminSubscriptionRefundRequest[]>([])
  const [refundRequestsTotalCount, setRefundRequestsTotalCount] = useState(0)
  const [selectedRefundRequest, setSelectedRefundRequest] = useState<AdminSubscriptionRefundRequest | null>(null)
  const [isRefundRequestDetailOpen, setIsRefundRequestDetailOpen] = useState(false)
  const [pendingRefundStatus, setPendingRefundStatus] = useState<SubscriptionRefundRequestStatus | null>(null)
  const translatedLanguageOptions = [
    { value: 'pt', label: t('languagePortuguese') },
    { value: 'en', label: t('languageEnglish') },
    { value: 'es', label: t('languageSpanish') },
    { value: 'fr', label: t('languageFrench') },
  ] as const
  const translatedTreatmentPreferenceOptions = [
    { value: UserTreatmentPreference.Masculine, label: t('treatmentPreferenceMasculine') },
    { value: UserTreatmentPreference.Feminine, label: t('treatmentPreferenceFeminine') },
  ] as const
  const subscriptionStatusQuery = subscriptionStatusFilter === 'all' ? undefined : subscriptionStatusFilter
  const subscriptionTierQuery = subscriptionTierFilter === 'all' ? undefined : subscriptionTierFilter

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    api
      .apiAdminUsersSubscriptionsGet(subsPage, subsPerPage, subscriptionStatusQuery, subscriptionTierQuery)
      .then(result => {
        const items = result?.items ?? []
        setSubscriptions(items)
        setSubscriptionsTotalCount(result?.totalCount ?? items.length)
      })
      .catch(err => {
        console.error('Erro ao carregar subscrições:', err);
      });
  }, [api, auth.isAuthenticated, subsPage, subsPerPage, subscriptionStatusQuery, subscriptionTierQuery]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    api
      .apiSubscriptionPlans("EUR")
      .then(plans => {
        setSubscriptionPlans(plans ?? [])
      })
      .catch(err => {
        console.error('Erro ao carregar planos:', err);
      });
  }, [api, auth.isAuthenticated]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    getAdminSubscriptionRefundRequests(api, refundRequestsPage, refundRequestsPerPage)
      .then(result => {
        setRefundRequests(result?.items ?? [])
        setRefundRequestsTotalCount(result?.totalCount ?? 0)
      })
      .catch(err => {
        console.error('Erro ao carregar pedidos de reembolso:', err)
      })
  }, [api, auth.isAuthenticated, refundRequestsPage, refundRequestsPerPage]);

  const matchesUserRegistrationDateFilter = (createdAt: string) => {
    if (userRegistrationDateFilter === 'all') return true

    const registeredAt = new Date(createdAt)
    if (Number.isNaN(registeredAt.getTime())) return false

    if (userRegistrationDateFilter === 'custom') {
      const registeredDate = registeredAt.toISOString().slice(0, 10)

      if (userRegistrationStartDate && registeredDate < userRegistrationStartDate) return false
      if (userRegistrationEndDate && registeredDate > userRegistrationEndDate) return false

      return true
    }

    const threshold = new Date()
    threshold.setHours(0, 0, 0, 0)
    threshold.setDate(threshold.getDate() - (userRegistrationDateFilter === 'last7Days' ? 6 : 29))

    return registeredAt >= threshold
  }

  const normalizedUserSearchTerm = searchTerm.trim().toLowerCase()
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      normalizedUserSearchTerm.length === 0 ||
      user.name.toLowerCase().includes(normalizedUserSearchTerm) ||
      user.email.toLowerCase().includes(normalizedUserSearchTerm)

    const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter
    const matchesStatus = userStatusFilter === 'all' || user.status === userStatusFilter

    return matchesSearch && matchesRole && matchesStatus && matchesUserRegistrationDateFilter(user.createdAt)
  })
  const totalUserPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage))
  const paginatedUsers = filteredUsers.slice((usersPage - 1) * usersPerPage, usersPage * usersPerPage)

  const normalizedSubscriptionSearchTerm = subscriptionSearchTerm.trim().toLowerCase()
  const filteredSubscriptions = subscriptions.filter(
    (sub) =>
      normalizedSubscriptionSearchTerm.length === 0 ||
      sub.user?.name?.toLowerCase().includes(normalizedSubscriptionSearchTerm)
  )

  const subscriptionPaginationTotal = normalizedSubscriptionSearchTerm.length === 0
    ? subscriptionsTotalCount
    : filteredSubscriptions.length
  const totalSubsPages = Math.max(1, Math.ceil(subscriptionPaginationTotal / subsPerPage))
  const paginatedSubscriptions = filteredSubscriptions
  const subscriptionsTitleKey = subscriptionStatusFilter === UserSubscriptionStatus.Active
    ? 'adminSubscriptionsActiveCount'
    : subscriptionStatusFilter === UserSubscriptionStatus.Canceled
      ? 'adminSubscriptionsCanceledCount'
      : 'adminSubscriptionsCount'

  const filteredRefundRequests = refundRequests.filter(
    (request) =>
      (request.userName ?? '').toLowerCase().includes(refundRequestSearchTerm.toLowerCase()) ||
      (request.contactEmail ?? '').toLowerCase().includes(refundRequestSearchTerm.toLowerCase()) ||
      (request.planName ?? '').toLowerCase().includes(refundRequestSearchTerm.toLowerCase()) ||
      (request.reason ?? '').toLowerCase().includes(refundRequestSearchTerm.toLowerCase()),
  )

  const totalRefundRequestPages = Math.max(1, Math.ceil(refundRequestsTotalCount / refundRequestsPerPage))
  const paginatedRefundRequests = filteredRefundRequests

  const getLanguageLabel = (code: string) => {
    if (!code) return '-'
    const match = translatedLanguageOptions.find(option => option.value === code)
    return match ? match.label : code
  }

  const getTreatmentPreferenceLabel = (value?: UserTreatmentPreference) => {
    const match = translatedTreatmentPreferenceOptions.find(option => option.value === value)
    return match ? match.label : t('treatmentPreferenceMasculine')
  }

  const getUserRoleLabel = (role: 'user' | 'admin') =>
    role === 'admin' ? t('adminRoleAdmin') : t('adminRoleUser')

  const getUserStatusLabel = (status: 'active' | 'inactive') =>
    status === 'active' ? t('adminStatusActive') : t('adminStatusInactive')

  const formatCreditAmount = (value?: number) =>
    typeof value === 'number'
      ? value.toLocaleString('pt-PT')
      : t('adminNotAvailable')

  const formatCreditRenewal = (value?: string) => {
    if (!value) return null

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null

    return parsed.toLocaleDateString('pt-PT')
  }

  const resetUserFilters = () => {
    setUserRoleFilter('all')
    setUserStatusFilter('all')
    setUserRegistrationDateFilter('all')
    setUserRegistrationStartDate('')
    setUserRegistrationEndDate('')
    setUsersPage(1)
  }

  const getSubscriptionIntervalLabel = (interval?: SubscriptionInterval) => {
    if (interval === SubscriptionInterval.Days) return t('adminIntervalDaily')
    if (interval === SubscriptionInterval.Months) return t('adminIntervalMonthly')
    if (interval === SubscriptionInterval.Years) return t('adminIntervalYearly')
    return t('adminNotAvailable')
  }

  const getSubscriptionStatusLabel = (status?: UserSubscriptionStatus) => {
    if (status === UserSubscriptionStatus.Active) return t('adminSubscriptionStatusActive')
    if (status === UserSubscriptionStatus.Trial) return t('adminSubscriptionStatusTrial')
    if (status === UserSubscriptionStatus.Canceled) return t('adminSubscriptionStatusCanceled')
    return t('adminSubscriptionStatusExpired')
  }

  const getJobStatusLabel = (status: AiReadingJobStatus) => {
    if (status === 'Failed') return t('adminJobStatusFailed')
    if (status === 'Processing') return t('adminJobStatusProcessing')
    if (status === 'Completed') return t('adminJobStatusCompleted')
    if (status === 'Canceled') return t('adminJobStatusCanceled')
    return t('adminJobStatusPending')
  }

  const getRefundRequestStatusLabel = (status?: SubscriptionRefundRequestStatus) => {
    if (status === 'Processed') return t('adminRefundRequestStatusProcessed')
    if (status === 'Rejected') return t('adminRefundRequestStatusRejected')
    return t('adminRefundRequestStatusPending')
  }

  const getPlanTierLabel = (tier?: SubscriptionTier | null) =>
    tier ? String(tier) : t('adminNotAvailable')

  const formatAiBudgetUsd = (value?: number | null) =>
    typeof value === 'number'
      ? value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })
      : t('adminNotAvailable')

  const formatAiBudgetNumber = (value?: number | null) =>
    typeof value === 'number'
      ? value.toLocaleString('pt-PT')
      : t('adminNotAvailable')

  const formatAiBudgetDate = (value?: Date | string | null) => {
    if (!value) return t('adminNotAvailable')

    const parsed = value instanceof Date
      ? value
      : new Date(value.length <= 10 ? `${value}T00:00:00.000Z` : value)

    return Number.isNaN(parsed.getTime())
      ? t('adminNotAvailable')
      : parsed.toLocaleDateString('pt-PT')
  }

  useEffect(() => {
    if (usersPage > totalUserPages) {
      setUsersPage(totalUserPages)
    }
  }, [usersPage, totalUserPages])

  useEffect(() => {
    if ((!isCreateDialogOpen && !isEditDialogOpen)) {
      setBirthPlaceSuggestions([])
      setIsSearchingBirthPlace(false)
      setBirthPlaceError(null)
      return
    }

    const query = birthPlaceQuery.trim()
    if (query.length < 3) {
      setBirthPlaceSuggestions([])
      setIsSearchingBirthPlace(false)
      setBirthPlaceError(null)
      return
    }

    let isActive = true
    setBirthPlaceError(null)
    const timeoutId = window.setTimeout(() => {
      setIsSearchingBirthPlace(true)
      searchBirthPlaceSuggestions(api, query)
        .then(results => {
          if (isActive) setBirthPlaceSuggestions(results)
        })
        .catch(err => {
          if (!isActive) return
          console.error('Erro ao procurar locais:', err)
          setBirthPlaceError(t('adminBirthPlaceLoadError'))
        })
        .finally(() => {
          if (isActive) setIsSearchingBirthPlace(false)
        })
    }, 400)

    return () => {
      isActive = false
      clearTimeout(timeoutId)
    }
  }, [api, birthPlaceQuery, isCreateDialogOpen, isEditDialogOpen, t])

  const handleBirthPlaceSelect = (suggestion: LocationSuggestion) => {
    setFormData(prev => ({
      ...prev,
      birthPlace: suggestion.displayName,
      birthLatitude: suggestion.latitude,
      birthLongitude: suggestion.longitude,
    }))
    setBirthPlaceQuery(suggestion.displayName)
    setBirthPlaceSuggestions([])
    setBirthPlaceError(null)
  }

  // Administration edits someone else's record, but a birth date in the future is
  // impossible there too.
  const today = maxBirthDate()
  const birthDateResult = parseBirthDate(formData.birthDate, { max: today })
  const birthTimeResult = parseBirthTime(formData.birthTime)
  const isBirthPairEmpty = isEmptyBirthDateDraft(formData.birthDate) && !formData.birthTime.trim()
  const isBirthPairComplete =
    birthDateResult.status === 'valid' && birthTimeResult.status === 'valid'
  const isBirthPairAcceptedForCreate = isBirthPairEmpty || isBirthPairComplete
  // Only one side filled in: the pair message replaces a per-field error that the
  // components cannot produce, because neither field is required on creation.
  const isBirthPairPartial =
    (isEmptyBirthDateDraft(formData.birthDate) && birthTimeResult.status === 'valid') ||
    (!formData.birthTime.trim() && birthDateResult.status === 'valid')

  const handleCreateUser = () => {
    // Local creation is the only place where the pair may stay completely empty; a
    // half-filled pair blocks the action instead of being completed silently.
    if (!isBirthPairAcceptedForCreate) {
      setShowBirthErrors(true)
      return
    }

    const newUser: User = {
      id: '',
      authId: '',
      name: formData.name,
      email: formData.email,
      birthDate: birthDateResult.status === 'valid' ? birthDateResult.value : '',
      birthTime: birthTimeResult.status === 'valid' ? birthTimeResult.value : '',
      birthPlace: formData.birthPlace,
      birthLatitude: formData.birthLatitude,
      birthLongitude: formData.birthLongitude,
      language: formData.language,
      treatmentPreference: formData.treatmentPreference,
      role: formData.role,
      createdAt: new Date().toISOString().split("T")[0],
      status: "active",
    }
    setUsers([...users, newUser])
    setIsCreateDialogOpen(false)
    resetForm()
  }

  const handleEditUser = () => {
    if (!selectedUser) return;
    // AdminUserUpdateDto.BirthDateTime is not nullable, so the edit sent to the API
    // requires both values.
    if (birthDateResult.status !== 'valid' || birthTimeResult.status !== 'valid') {
      setShowBirthErrors(true)
      return
    }

    const payload = new AdminUserUpdateDto();
    payload.authId = selectedUser.authId;
    payload.name = formData.name;

    const birthDateTimeValue = buildUtcDatePreservingClock(birthDateResult.value, birthTimeResult.value);
    if (!birthDateTimeValue) return;

    payload.birthDateTime = birthDateTimeValue;
    payload.birthPlace = formData.birthPlace || undefined;
    payload.birthLatitude = formData.birthLatitude ?? undefined;
    payload.birthLongitude = formData.birthLongitude ?? undefined;
    payload.language = formData.language;
    payload.treatmentPreference = formData.treatmentPreference;
    payload.bio = '';
    payload.isPrivate = true;

    api
      .apiAdminUsersPut(selectedUser.authId, payload)
      .then(() => {
        setUsers(prev =>
          prev.map(u =>
            u.authId === selectedUser.authId
              ? {
                ...u,
                name: formData.name,
                email: formData.email,
                birthDate: birthDateResult.value,
                birthTime: birthTimeResult.value,
                birthPlace: formData.birthPlace,
                language: formData.language,
                treatmentPreference: formData.treatmentPreference,
                role: formData.role,
              }
              : u
          )
        );

        setIsEditDialogOpen(false);
        resetForm();
      })
      .catch(err => {
        console.error('Erro ao atualizar utilizador:', err);
      });
  };

  const handleDeleteUser = () => {
    if (!selectedUser) return
    setUsers(users.filter((user) => user.id !== selectedUser.id))
    setIsDeleteDialogOpen(false)
    setSelectedUser(null)
  }

  const openEditDialog = (user: User) => {
    setSelectedUser(user)
    setFormData({
      name: user.name,
      email: user.email,
      birthDate: toBirthDateDraft(user.birthDate),
      birthTime: user.birthTime,
      birthPlace: user.birthPlace,
      birthLatitude: user.birthLatitude,
      birthLongitude: user.birthLongitude,
      language: user.language || 'pt',
      treatmentPreference: user.treatmentPreference || UserTreatmentPreference.Masculine,
      role: user.role,
    })
    setBirthPlaceQuery("")
    setBirthPlaceSuggestions([])
    setBirthPlaceError(null)
    setIsEditDialogOpen(true)
  }

  const handleCancelSubscription = async () => {
    if (!selectedSubscription) return
    if (!auth.isAuthenticated) return

    try {
      await api.apiAdminUsersSubscriptionsCancel(selectedSubscription.userId ?? '')

      // Refresh global subscription state
      await refreshSubscription()

      // Refresh subscriptions list
      const result = await api.apiAdminUsersSubscriptionsGet(subsPage, subsPerPage, subscriptionStatusQuery, subscriptionTierQuery)
      const items = result?.items ?? []
      setSubscriptions(items)
      setSubscriptionsTotalCount(result?.totalCount ?? items.length)

      setIsCancelSubscriptionDialogOpen(false)
      setSelectedSubscription(null)
    } catch (err) {
      console.error('Erro ao cancelar subscrição:', err)
    }
  }

  const handleRefundRequestStatusUpdate = async (request: AdminSubscriptionRefundRequest, status: SubscriptionRefundRequestStatus) => {
    if (!auth.isAuthenticated) return

    setPendingRefundStatus(status)
    try {
      const updated = await updateAdminSubscriptionRefundRequest(api, request.id, { status })
      setRefundRequests(prev => prev.map(item => item.id === updated.id ? updated : item))
      if (selectedRefundRequest?.id === updated.id) {
        setSelectedRefundRequest(updated)
      }
    } catch (err) {
      console.error('Erro ao atualizar pedido de reembolso:', err)
    } finally {
      setPendingRefundStatus(null)
    }
  }

  const [jobSearchTerm, setJobSearchTerm] = useState("")
  const [jobsPerPage, setJobsPerPage] = useState(10)
  const [jobsPage, setJobsPage] = useState(1)
  const [failedJobs, setFailedJobs] = useState<AiReadingJob[]>([])
  const [jobsTotalCount, setJobsTotalCount] = useState(0)
  const [selectedJob, setSelectedJob] = useState<AiReadingJob | null>(null)
  const [isJobDetailOpen, setIsJobDetailOpen] = useState(false)
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false)
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null)
  const [isCancelJobDialogOpen, setIsCancelJobDialogOpen] = useState(false)
  const [cancelingJobId, setCancelingJobId] = useState<string | null>(null)
  const [aiBudgetDate, setAiBudgetDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [aiBudgetRefreshKey, setAiBudgetRefreshKey] = useState(0)
  const [aiBudgetSnapshot, setAiBudgetSnapshot] = useState<AdminAiBudgetSnapshot | null>(null)
  const [isAiBudgetLoading, setIsAiBudgetLoading] = useState(false)
  const [aiBudgetError, setAiBudgetError] = useState<string | null>(null)

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    api
      .apiAdminUsersFailedJobs(jobsPage, jobsPerPage)
      .then(result => {
        const items = result?.items ?? []
        const mappedJobs: AiReadingJob[] = items.map((j) => ({
          id: j.id ?? '',
          natalChartId: j.natalChartId || '',
          context: j.context || '',
          profileKey: j.profileKey || null,
          date: j.date ? new Date(j.date).toISOString() : null,
          startDay: j.startDay ? new Date(j.startDay).toISOString() : null,
          endDay: j.endDay ? new Date(j.endDay).toISOString() : null,
          language: j.language || 'pt',
          treatmentPreference: j.treatmentPreference ?? UserTreatmentPreference.Masculine,
          schemaVersion: j.schemaVersion || '1.0',
          status: j.status as AiReadingJobStatus,
          batchId: null,
          error: j.error || null,
          attempts: j.attempts || 0,
          createdAtUtc: j.createdAtUtc ? new Date(j.createdAtUtc).toISOString() : new Date().toISOString(),
          updatedAtUtc: null
        }))
        setFailedJobs(mappedJobs)
        setJobsTotalCount(result?.totalCount ?? items.length)
      })
      .catch((err) => {
        console.error('Erro ao carregar jobs falhados:', err);
      });
  }, [api, auth.isAuthenticated, jobsPage, jobsPerPage]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    let isActive = true
    const adminBudgetApi = api as unknown as AdminAiBudgetApiClient

    const selectedDate = aiBudgetDate
      ? new Date(`${aiBudgetDate}T00:00:00.000Z`)
      : undefined
    let budgetRequest: Promise<AdminAiBudgetSnapshot>

    if (typeof adminBudgetApi.apiAdminAiBudgetGet === 'function') {
      budgetRequest = adminBudgetApi.apiAdminAiBudgetGet(selectedDate)
    } else if (adminBudgetApi.http?.fetch) {
      let url = `${adminBudgetApi.baseUrl ?? ''}/api/AdminAiBudget`
      if (aiBudgetDate) {
        url += `?date=${encodeURIComponent(aiBudgetDate)}`
      }

      budgetRequest = adminBudgetApi.http.fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error(`AI budget request failed with status ${response.status}`)
        }

        const text = await response.text()
        return text ? JSON.parse(text) as AdminAiBudgetSnapshot : {}
      })
    } else {
      setAiBudgetSnapshot(null)
      setAiBudgetError(t('adminAiBudgetClientUnavailable'))
      setIsAiBudgetLoading(false)
      return
    }

    setIsAiBudgetLoading(true)
    setAiBudgetError(null)

    budgetRequest
      .then(snapshot => {
        if (!isActive) return
        setAiBudgetSnapshot(snapshot)
      })
      .catch((err) => {
        if (!isActive) return
        console.error('Erro ao carregar budget de IA:', err)
        setAiBudgetError(t('adminAiBudgetLoadError'))
      })
      .finally(() => {
        if (isActive) setIsAiBudgetLoading(false)
      })

    return () => {
      isActive = false
    }
  }, [api, auth.isAuthenticated, aiBudgetDate, aiBudgetRefreshKey, t]);

  const filteredJobs = failedJobs.filter(
    (job) =>
      job.id.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
      job.context.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
      (job.profileKey && job.profileKey.toLowerCase().includes(jobSearchTerm.toLowerCase())) ||
      (job.error && job.error.toLowerCase().includes(jobSearchTerm.toLowerCase())) ||
      (job.natalChartId && job.natalChartId.toLowerCase().includes(jobSearchTerm.toLowerCase())),
  )

  const totalJobsPages = Math.max(1, Math.ceil(jobsTotalCount / jobsPerPage))
  const paginatedJobs = filteredJobs
  const aiBudgetContexts = aiBudgetSnapshot?.contexts ?? []
  const aiBudgetUsers = aiBudgetSnapshot?.users ?? []

  const handleRetryJob = async (jobId: string) => {
    if (!auth.isAuthenticated) return;
    setRetryingJobId(jobId);
    try {
      await api.apiAdminUsersFailedJobsRetry(jobId);
      setFailedJobs(prev => prev.filter(j => j.id !== jobId));
      setJobsTotalCount(prev => Math.max(0, prev - 1));
      setIsRetryDialogOpen(false);
      setSelectedJob(null);
    } catch (err) {
      console.error('Erro ao re-executar job:', err);
    } finally {
      setRetryingJobId(null);
    }
  }

  const handleCancelJob = async (jobId: string) => {
    if (!auth.isAuthenticated) return;
    setCancelingJobId(jobId);
    try {
      await api.apiAdminUsersFailedJobsCancel(jobId);
      setFailedJobs(prev => prev.filter(j => j.id !== jobId));
      setJobsTotalCount(prev => Math.max(0, prev - 1));
      setIsCancelJobDialogOpen(false);
      setSelectedJob(null);
    } catch (err) {
      console.error('Erro ao cancelar job:', err);
    } finally {
      setCancelingJobId(null);
    }
  }

  const renderBirthPlaceField = (inputId: string) => {
    const showFeedback = (isCreateDialogOpen || isEditDialogOpen) && birthPlaceQuery.trim().length >= 3
    return (
      <div className="space-y-2">
        <Label htmlFor={inputId}>{t('birthPlace')}</Label>
        <div className="space-y-2">
          <Input
            id={inputId}
            placeholder={t('adminBirthPlacePlaceholder')}
            value={formData.birthPlace}
            onChange={(e) => {
              const value = e.target.value
              setFormData({
                ...formData,
                birthPlace: value,
                birthLatitude: undefined,
                birthLongitude: undefined,
              })
              setBirthPlaceQuery(value)
            }}
          />
          {showFeedback && (
            <div className="space-y-2">
              {isSearchingBirthPlace && (
                <p className="text-xs text-slate-500">{t('adminSearchingLocations')}</p>
              )}
              {birthPlaceError && (
                <p className="text-xs text-red-500">{birthPlaceError}</p>
              )}
              {!isSearchingBirthPlace && !birthPlaceError && birthPlaceSuggestions.length > 0 && (
                <div className="border border-slate-200 rounded-md divide-y bg-white shadow-sm max-h-48 overflow-y-auto">
                  {birthPlaceSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-action-50"
                      onClick={() => handleBirthPlaceSelect(suggestion)}
                    >
                      <span className="block">{suggestion.displayName}</span>
                      <span className="text-xs text-slate-500">
                        {t('adminCoordinates', { lat: suggestion.latitude.toFixed(4), lon: suggestion.longitude.toFixed(4) })}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user)
    setIsDeleteDialogOpen(true)
  }

  const openTerminateSessionsDialog = (user: User) => {
    setSelectedUser(user)
    setIsTerminateSessionsDialogOpen(true)
  }

  const handleTerminateSessions = async () => {
    if (!selectedUser) return
    if (!auth.isAuthenticated) return;
    await api
      .apiAdminUsersRevokeSessions(selectedUser.authId)
      .then(() => {
        console.log(`Terminating all sessions for user: ${selectedUser.name}`)
      })
      .catch(err => {
        console.error('Erro ao revogar sessões do utilizador:', err);
      });

    setIsTerminateSessionsDialogOpen(false)
    setSelectedUser(null)
  }

  const resetForm = () => {
    setShowBirthErrors(false)
    setFormData({
      name: "",
      email: "",
      birthDate: EMPTY_BIRTH_DATE_DRAFT,
      birthTime: "",
      birthPlace: "",
      birthLatitude: undefined,
      birthLongitude: undefined,
      language: "pt",
      treatmentPreference: UserTreatmentPreference.Masculine,
      role: "user",
    })
    setBirthPlaceQuery("")
    setBirthPlaceSuggestions([])
    setBirthPlaceError(null)
    setIsSearchingBirthPlace(false)
    setSelectedUser(null)
  }

  const openViewSubscriptionDialog = (subscription: UserSubscription) => {
    setSelectedSubscription(subscription)
    setIsViewSubscriptionDialogOpen(true)
  }

  const openCancelDialog = (subscription: UserSubscription) => {
    setSelectedSubscription(subscription)
    setIsCancelSubscriptionDialogOpen(true)
  }

  const handleCreateSubscription = async () => {
    if (!auth.isAuthenticated) return

    if (!subscriptionFormData.userId || !subscriptionFormData.planId) {
      console.error('Utilizador e plano são obrigatórios para criar subscrição.')
      return
    }

    const payload = new AdminCreateSubscriptionDto()
    payload.userId = subscriptionFormData.userId
    payload.planCode = subscriptionFormData.planId

    try {
      await api.apiAdminUsersSubscriptionsManual(payload)

      // Refresh global subscription state
      await refreshSubscription()

      const result = await api.apiAdminUsersSubscriptionsGet(subsPage, subsPerPage, subscriptionStatusQuery, subscriptionTierQuery)
      const items = result?.items ?? []
      setSubscriptions(items)
      setSubscriptionsTotalCount(result?.totalCount ?? items.length)

      setIsCreateSubscriptionDialogOpen(false)
      resetSubscriptionForm()
    } catch (err) {
      console.error('Erro ao criar subscrição manual:', err)
    }
  }

  const resetSubscriptionForm = () => {
    setSubscriptionFormData({
      userId: "",
      planId: "",
      status: UserSubscriptionStatus.Active,
      endsAtUtc: "",
    })
    setCreateSubUserSearch("")
    setIsUserDropdownOpen(false)
    setSelectedSubscription(null)
  }

  return (
    <IonPage>
      <IonContent fullscreen className="app-safe-content bg-gradient-to-br from-slate-50 via-stone-100 to-gray-100">
        <div className="app-safe-screen min-h-screen">
          <header className="app-safe-top-offset border-stone-200 bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-40">
            <div className="container mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Link to="/">
                    <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-800">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      {t('goBack')}
                    </Button>
                  </Link>
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <Star className="h-8 w-8 text-amber-500" />
                      <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-action-500" />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-slate-800 flex items-center">
                        <Shield className="h-6 w-6 mr-2 text-action-500" />
                        {t('adminTitle')}
                      </h1>
                      <p className="text-sm text-slate-600">{t('adminSubtitle')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="container mx-auto px-4 py-6">
            <Tabs defaultValue="users" className="space-y-4">
              <div>
                <TabsList className="grid h-auto w-full grid-cols-2 gap-1 bg-stone-100 p-1 sm:grid-cols-3 md:grid-cols-5 md:gap-0">
                  <TabsTrigger
                    value="users"
                    className="h-auto min-h-11 w-full whitespace-normal px-2 py-1.5 text-center leading-tight data-[state=active]:bg-action-100 data-[state=active]:text-action-800 md:px-4"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    {t('adminTabUsers')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="subscriptions"
                    className="h-auto min-h-11 w-full whitespace-normal px-2 py-1.5 text-center leading-tight data-[state=active]:bg-action-100 data-[state=active]:text-action-800 md:px-4"
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t('adminTabSubscriptions')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="refund-requests"
                    className="h-auto min-h-11 w-full whitespace-normal px-2 py-1.5 text-center leading-tight data-[state=active]:bg-action-100 data-[state=active]:text-action-800 md:px-4"
                  >
                    <LifeBuoy className="mr-2 h-4 w-4" />
                    {t('adminTabRefundRequests')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="ai-budget"
                    className="h-auto min-h-11 w-full whitespace-normal px-2 py-1.5 text-center leading-tight data-[state=active]:bg-action-100 data-[state=active]:text-action-800 md:px-4"
                  >
                    <Gauge className="mr-2 h-4 w-4" />
                    {t('adminTabAiBudget')}
                  </TabsTrigger>
                  <TabsTrigger
                    value="failed-jobs"
                    className="h-auto min-h-11 w-full whitespace-normal px-2 py-1.5 text-center leading-tight data-[state=active]:bg-action-100 data-[state=active]:text-action-800 md:px-4"
                  >
                    <FileWarning className="mr-2 h-4 w-4" />
                    {t('adminTabFailedJobs')}
                    {failedJobs.filter((j) => j.status === "Failed").length > 0 && (
                      <Badge className="ml-2 bg-red-500 px-1.5 py-0 text-xs text-white">{failedJobs.filter((j) => j.status === "Failed").length}</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="users">
                <Card className="bg-white border-stone-200 shadow-lg">
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                      <CardTitle className="text-xl text-slate-800 flex items-center">
                        <Users className="h-5 w-5 mr-2 text-action-500" />
                        {t('adminUsersRegisteredCount', { count: users.length })}
                      </CardTitle>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1 sm:flex-initial">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder={t('adminSearchUsers')}
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value)
                              setUsersPage(1)
                            }}
                            className="pl-10 w-full sm:w-64"
                          />
                        </div>
                        <Button
                          onClick={() => setIsCreateDialogOpen(true)}
                          className="bg-action-700 hover:bg-action-800 text-white"
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          {t('adminCreateUser')}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4 grid grid-cols-1 gap-3 border-b border-stone-200 pb-4 md:grid-cols-2 xl:grid-cols-5">
                      <div className="space-y-2">
                        <Label htmlFor="user-role-filter" className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          {t('adminTableRole')}
                        </Label>
                        <Select
                          value={userRoleFilter}
                          onValueChange={(value: UserRoleFilter) => {
                            setUserRoleFilter(value)
                            setUsersPage(1)
                          }}
                        >
                          <SelectTrigger id="user-role-filter" className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="all">{t('adminFilterAll')}</SelectItem>
                            <SelectItem value="user">{t('adminRoleUser')}</SelectItem>
                            <SelectItem value="admin">{t('adminRoleAdmin')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="user-status-filter" className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          {t('adminTableStatus')}
                        </Label>
                        <Select
                          value={userStatusFilter}
                          onValueChange={(value: UserStatusFilter) => {
                            setUserStatusFilter(value)
                            setUsersPage(1)
                          }}
                        >
                          <SelectTrigger id="user-status-filter" className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="all">{t('adminFilterAll')}</SelectItem>
                            <SelectItem value="active">{t('adminStatusActive')}</SelectItem>
                            <SelectItem value="inactive">{t('adminStatusInactive')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="user-registration-filter" className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          {t('adminTableRegistered')}
                        </Label>
                        <Select
                          value={userRegistrationDateFilter}
                          onValueChange={(value: UserRegistrationDateFilter) => {
                            setUserRegistrationDateFilter(value)
                            setUsersPage(1)
                          }}
                        >
                          <SelectTrigger id="user-registration-filter" className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="all">{t('adminFilterAll')}</SelectItem>
                            <SelectItem value="last7Days">{t('adminRegistrationLast7Days')}</SelectItem>
                            <SelectItem value="last30Days">{t('adminRegistrationLast30Days')}</SelectItem>
                            <SelectItem value="custom">{t('adminRegistrationCustomRange')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {userRegistrationDateFilter === 'custom' && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="user-registration-start" className="text-xs font-medium uppercase tracking-wide text-slate-500">
                              {t('adminRegistrationStartDate')}
                            </Label>
                            <Input
                              id="user-registration-start"
                              type="date"
                              value={userRegistrationStartDate}
                              onChange={(e) => {
                                setUserRegistrationStartDate(e.target.value)
                                setUsersPage(1)
                              }}
                              className="bg-white"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="user-registration-end" className="text-xs font-medium uppercase tracking-wide text-slate-500">
                              {t('adminRegistrationEndDate')}
                            </Label>
                            <Input
                              id="user-registration-end"
                              type="date"
                              value={userRegistrationEndDate}
                              onChange={(e) => {
                                setUserRegistrationEndDate(e.target.value)
                                setUsersPage(1)
                              }}
                              className="bg-white"
                            />
                          </div>
                        </>
                      )}

                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={resetUserFilters}
                          className="w-full"
                        >
                          {t('adminClearFilters')}
                        </Button>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('name')}</TableHead>
                            <TableHead>{t('email')}</TableHead>
                            <TableHead>{t('adminBirthDate')}</TableHead>
                            <TableHead>{t('adminBirthTime')}</TableHead>
                            <TableHead>{t('birthPlace')}</TableHead>
                            <TableHead>{t('language')}</TableHead>
                            <TableHead>{t('treatmentPreference')}</TableHead>
                            <TableHead>{t('adminTableRole')}</TableHead>
                            <TableHead>{t('adminTableStatus')}</TableHead>
                            <TableHead>{t('adminTableCredits')}</TableHead>
                            <TableHead>{t('adminTableRegistered')}</TableHead>
                            <TableHead className="text-right">{t('adminTableActions')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody
                          key={[
                            normalizedUserSearchTerm,
                            userRoleFilter,
                            userStatusFilter,
                            userRegistrationDateFilter,
                            userRegistrationStartDate,
                            userRegistrationEndDate,
                            usersPage,
                            usersPerPage,
                          ].join('|')}
                        >
                          {paginatedUsers.map((user) => (
                            <TableRow key={user.authId || user.id || user.email}>
                              <TableCell className="font-medium">{user.name}</TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>{formatCivilBirthDate(user.birthDate, "pt-PT") ?? '-'}</TableCell>
                              <TableCell>{user.birthTime || '-'}</TableCell>
                              <TableCell>{user.birthPlace ? user.birthPlace : '-'}</TableCell>
                              <TableCell>{getLanguageLabel(user.language)}</TableCell>
                              <TableCell>{getTreatmentPreferenceLabel(user.treatmentPreference)}</TableCell>
                              <TableCell>
                                <Badge
                                  variant={user.role === "admin" ? "default" : "secondary"}
                                  className={user.role === "admin" ? "bg-action-700" : ""}
                                >
                                  {getUserRoleLabel(user.role)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={user.status === "active" ? "default" : "secondary"}
                                  className={user.status === "active" ? "bg-green-500" : "bg-slate-400"}
                                >
                                  {getUserStatusLabel(user.status)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  <Badge
                                    variant={typeof user.remainingCredits === 'number' && user.remainingCredits > 0 ? 'secondary' : 'outline'}
                                    className={typeof user.remainingCredits === 'number' && user.remainingCredits > 0 ? 'bg-emerald-100 text-emerald-800' : ''}
                                  >
                                    {formatCreditAmount(user.remainingCredits)}
                                  </Badge>
                                  {typeof user.usedCredits === 'number' && typeof user.includedCredits === 'number' ? (
                                    <div className="text-xs text-slate-500">
                                      {t('adminCreditsUsage', { used: user.usedCredits, included: user.includedCredits })}
                                    </div>
                                  ) : null}
                                  {formatCreditRenewal(user.creditCycleEndsAtUtc) ? (
                                    <div className="text-xs text-slate-400">
                                      {t('adminCreditsRenewsOn', { date: formatCreditRenewal(user.creditCycleEndsAtUtc) })}
                                    </div>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell>{new Date(user.createdAt).toLocaleDateString("pt-PT")}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end space-x-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openTerminateSessionsDialog(user)}
                                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    title={t('adminTerminateSessionsAction')}
                                  >
                                    <LogOut className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditDialog(user)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openDeleteDialog(user)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {filteredUsers.length === 0 && (
                      <div className="text-center py-12">
                        <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">{t('adminNoUsersFound')}</p>
                      </div>
                    )}
                    {filteredUsers.length > 0 && (
                      <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-stone-200 mt-4 gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">{t('adminShow')}</span>
                          <Select
                            value={String(usersPerPage)}
                            onValueChange={(val) => {
                              setUsersPerPage(Number(val))
                              setUsersPage(1)
                            }}
                          >
                            <SelectTrigger className="w-[70px] h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="20">20</SelectItem>
                              <SelectItem value="40">40</SelectItem>
                            </SelectContent>
                          </Select>
                          <span className="text-sm text-slate-600">{t('adminPerPage')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            {t('adminPaginationRange', {
                              start: (usersPage - 1) * usersPerPage + 1,
                              end: Math.min(usersPage * usersPerPage, filteredUsers.length),
                              total: filteredUsers.length,
                            })}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={usersPage <= 1}
                            onClick={() => setUsersPage((page) => page - 1)}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm font-medium text-slate-700">
                            {usersPage} / {totalUserPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={usersPage >= totalUserPages}
                            onClick={() => setUsersPage((page) => page + 1)}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="subscriptions" className="space-y-6">
                <Card className="bg-white border-stone-200 shadow-lg">
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <CardTitle className="text-xl text-slate-800 flex items-center shrink-0">
                        <CreditCard className="h-5 w-5 mr-2 text-action-500" />
                        {t(subscriptionsTitleKey, { count: subscriptionsTotalCount })}
                      </CardTitle>
                      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 w-full md:w-auto md:justify-end">
                        <Select
                          value={subscriptionStatusFilter}
                          onValueChange={(val) => {
                            setSubscriptionStatusFilter(val as SubscriptionStatusFilter)
                            setSubsPage(1)
                          }}
                        >
                          <SelectTrigger className="w-full sm:w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="all">{t('adminSubscriptionStatusAll')}</SelectItem>
                            <SelectItem value={UserSubscriptionStatus.Active}>{t('adminSubscriptionStatusActive')}</SelectItem>
                            <SelectItem value={UserSubscriptionStatus.Canceled}>{t('adminSubscriptionStatusCanceled')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select
                          value={subscriptionTierFilter}
                          onValueChange={(val) => {
                            setSubscriptionTierFilter(val as SubscriptionTierFilter)
                            setSubsPage(1)
                          }}
                        >
                          <SelectTrigger className="w-full sm:w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="all">{t('adminSubscriptionTierAll')}</SelectItem>
                            <SelectItem value={SubscriptionTier.Basic}>Basic</SelectItem>
                            <SelectItem value={SubscriptionTier.Pro}>Pro</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="relative flex-1 sm:flex-initial">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder={t('adminSearchSubscriptions')}
                            value={subscriptionSearchTerm}
                            onChange={(e) => { setSubscriptionSearchTerm(e.target.value); setSubsPage(1) }}
                            className="pl-10 w-full sm:w-64"
                          />
                        </div>
                        <Button
                          onClick={() => setIsCreateSubscriptionDialogOpen(true)}
                          className="bg-action-700 hover:bg-action-800 text-white"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {t('adminCreateSubscription')}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('adminTableUser')}</TableHead>
                            <TableHead>{t('adminTablePlan')}</TableHead>
                            <TableHead>{t('adminTableBilling')}</TableHead>
                            <TableHead>{t('adminTableAmount')}</TableHead>
                            <TableHead>{t('adminTableStatus')}</TableHead>
                            <TableHead>{t('adminTableStartDate')}</TableHead>
                            <TableHead>{t('adminTableEndDate')}</TableHead>
                            <TableHead className="text-right">{t('adminTableActions')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedSubscriptions.map((subscription) => (
                            <TableRow key={subscription.id}>
                              <TableCell className="font-medium">{subscription.user?.name}</TableCell>
                              <TableCell>
                                <Badge
                                  variant={subscription.plan?.tier === SubscriptionTier.Pro ? "default" : "secondary"}
                                  className={subscription.plan?.tier === SubscriptionTier.Pro ? "bg-purple-500" : "bg-blue-500"}
                                >
                                  {subscription.plan?.name}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="border-slate-300">
                                  {getSubscriptionIntervalLabel(subscription.plan?.interval)}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-semibold">{subscription.plan?.price?.toFixed(2)}€</TableCell>
                              <TableCell>
                                <Badge
                                  variant={subscription.status === UserSubscriptionStatus.Active || subscription.status === UserSubscriptionStatus.Trial ? "default" : "secondary"}
                                  className={
                                    subscription.status === UserSubscriptionStatus.Active || subscription.status === UserSubscriptionStatus.Trial
                                      ? "bg-green-500"
                                      : subscription.status === UserSubscriptionStatus.Canceled
                                        ? "bg-orange-500"
                                        : "bg-red-500"
                                  }
                                >
                                  {getSubscriptionStatusLabel(subscription.status)}
                                </Badge>
                              </TableCell>
                              <TableCell>{subscription.startsAtUtc?.toLocaleDateString("pt-PT")}</TableCell>
                              <TableCell>{subscription.endsAtUtc?.toLocaleDateString("pt-PT")}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end space-x-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openViewSubscriptionDialog(subscription)}
                                    className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
                                    title={t('adminViewDetails')}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openCancelDialog(subscription)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title={t('adminCancelSubscriptionAction')}
                                    disabled={subscription.status !== UserSubscriptionStatus.Active && subscription.status !== UserSubscriptionStatus.Trial}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {filteredSubscriptions.length === 0 && (
                      <div className="text-center py-12">
                        <CreditCard className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">{t('adminNoSubscriptionsFound')}</p>
                      </div>
                    )}
                    {filteredSubscriptions.length > 0 && (
                      <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-stone-200 mt-4 gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">{t('adminShow')}</span>
                          <Select
                            value={String(subsPerPage)}
                            onValueChange={(val) => {
                              setSubsPerPage(Number(val))
                              setSubsPage(1)
                            }}
                          >
                            <SelectTrigger className="w-[70px] h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="20">20</SelectItem>
                              <SelectItem value="40">40</SelectItem>
                            </SelectContent>
                          </Select>
                          <span className="text-sm text-slate-600">{t('adminPerPage')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            {t('adminPaginationRange', {
                              start: subscriptionPaginationTotal === 0 ? 0 : (subsPage - 1) * subsPerPage + 1,
                              end: Math.min(subsPage * subsPerPage, subscriptionPaginationTotal),
                              total: subscriptionPaginationTotal,
                            })}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={subsPage <= 1}
                            onClick={() => setSubsPage((p) => p - 1)}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm font-medium text-slate-700">
                            {subsPage} / {totalSubsPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={subsPage >= totalSubsPages}
                            onClick={() => setSubsPage((p) => p + 1)}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="refund-requests" className="space-y-6">
                <Card className="bg-white border-stone-200 shadow-lg">
                  <CardHeader>
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <CardTitle className="text-xl text-slate-800 flex items-center">
                        <LifeBuoy className="h-5 w-5 mr-2 text-amber-500" />
                        {t('adminRefundRequestsCount', { count: refundRequestsTotalCount })}
                      </CardTitle>
                      <div className="relative w-full md:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder={t('adminSearchRefundRequests')}
                          value={refundRequestSearchTerm}
                          onChange={(e) => setRefundRequestSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('adminTableUser')}</TableHead>
                            <TableHead>{t('adminTablePlan')}</TableHead>
                            <TableHead>{t('adminRefundRequestContact')}</TableHead>
                            <TableHead>{t('adminTableCreatedAt')}</TableHead>
                            <TableHead>{t('adminTableStatus')}</TableHead>
                            <TableHead className="text-right">{t('adminTableActions')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedRefundRequests.map((request) => (
                            <TableRow key={request.id}>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-medium text-slate-800">{request.userName || t('adminNotAvailable')}</span>
                                  {request.userPublicId ? (
                                    <span className="text-xs text-slate-500">@{request.userPublicId}</span>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell>{request.planName || t('adminNotAvailable')}</TableCell>
                              <TableCell>{request.contactEmail || t('adminNotAvailable')}</TableCell>
                              <TableCell>{request.createdAtUtc ? new Date(request.createdAtUtc).toLocaleDateString("pt-PT") : t('adminNotAvailable')}</TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    request.status === 'Processed'
                                      ? 'secondary'
                                      : request.status === 'Rejected'
                                        ? 'destructive'
                                        : 'outline'
                                  }
                                  className={
                                    request.status === 'Processed'
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : request.status === 'Pending'
                                        ? 'border-amber-200 text-amber-700'
                                        : ''
                                  }
                                >
                                  {getRefundRequestStatusLabel(request.status)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSelectedRefundRequest(request); setIsRefundRequestDetailOpen(true) }}
                                    className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
                                    title={t('adminViewDetails')}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRefundRequestStatusUpdate(request, 'Processed')}
                                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    title={t('adminRefundRequestMarkProcessed')}
                                    disabled={request.status !== 'Pending' || pendingRefundStatus !== null}
                                  >
                                    {pendingRefundStatus === 'Processed' ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Check className="h-4 w-4" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRefundRequestStatusUpdate(request, 'Rejected')}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title={t('adminRefundRequestReject')}
                                    disabled={request.status !== 'Pending' || pendingRefundStatus !== null}
                                  >
                                    {pendingRefundStatus === 'Rejected' ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <X className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {filteredRefundRequests.length === 0 && (
                      <div className="text-center py-12">
                        <LifeBuoy className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">{t('adminNoRefundRequestsFound')}</p>
                      </div>
                    )}

                    {refundRequestsTotalCount > 0 && (
                      <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-stone-200 mt-4 gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">{t('adminShow')}</span>
                          <Select
                            value={String(refundRequestsPerPage)}
                            onValueChange={(val) => {
                              setRefundRequestsPerPage(Number(val))
                              setRefundRequestsPage(1)
                            }}
                          >
                            <SelectTrigger className="w-[70px] h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="20">20</SelectItem>
                              <SelectItem value="40">40</SelectItem>
                            </SelectContent>
                          </Select>
                          <span className="text-sm text-slate-600">{t('adminPerPage')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            {t('adminPaginationRange', {
                              start: refundRequestsTotalCount === 0 ? 0 : ((refundRequestsPage - 1) * refundRequestsPerPage) + 1,
                              end: Math.min(refundRequestsPage * refundRequestsPerPage, refundRequestsTotalCount),
                              total: refundRequestsTotalCount,
                            })}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={refundRequestsPage <= 1}
                            onClick={() => setRefundRequestsPage((page) => page - 1)}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm font-medium text-slate-700">
                            {refundRequestsPage} / {totalRefundRequestPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={refundRequestsPage >= totalRefundRequestPages}
                            onClick={() => setRefundRequestsPage((page) => page + 1)}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="ai-budget">
                <Card className="bg-white border-stone-200 shadow-lg">
                  <CardHeader>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <CardTitle className="text-slate-800 flex items-center text-lg">
                          <Gauge className="h-5 w-5 mr-2 text-action-500" />
                          {t('adminAiBudgetTitle')}
                        </CardTitle>
                        <p className="text-sm text-slate-500 mt-1">
                          {t('adminAiBudgetDescription')}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                        <div className="space-y-1">
                          <Label htmlFor="ai-budget-date" className="text-xs text-slate-600">
                            {t('adminAiBudgetDateLabel')}
                          </Label>
                          <Input
                            id="ai-budget-date"
                            type="date"
                            value={aiBudgetDate}
                            onChange={(event) => setAiBudgetDate(event.target.value)}
                            className="h-9 w-full sm:w-44"
                          />
                        </div>
                        <Button
                          variant="outline"
                          className="h-9"
                          onClick={() => setAiBudgetRefreshKey((value) => value + 1)}
                          disabled={isAiBudgetLoading}
                        >
                          {isAiBudgetLoading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="mr-2 h-4 w-4" />
                          )}
                          {t('adminAiBudgetRefresh')}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {aiBudgetError && (
                      <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{aiBudgetError}</span>
                      </div>
                    )}

                    {isAiBudgetLoading && !aiBudgetSnapshot && (
                      <div className="flex items-center justify-center py-10 text-sm text-slate-500">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('adminAiBudgetLoading')}
                      </div>
                    )}

                    {aiBudgetSnapshot && (
                      <>
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <div className="rounded-md bg-stone-50 p-4">
                            <p className="text-xs font-medium uppercase text-slate-500">{t('adminAiBudgetOpenAiRequests')}</p>
                            <p className="mt-2 text-2xl font-semibold text-slate-900">{formatAiBudgetNumber(aiBudgetSnapshot.totalRequests)}</p>
                            <p className="mt-1 text-xs text-slate-500">{formatAiBudgetDate(aiBudgetSnapshot.date)}</p>
                          </div>
                          <div className="rounded-md bg-stone-50 p-4">
                            <p className="text-xs font-medium uppercase text-slate-500">{t('adminAiBudgetBasicRequests')}</p>
                            <p className={`mt-2 text-2xl font-semibold ${aiBudgetSnapshot.hasBasicRequests ? 'text-amber-700' : 'text-slate-900'}`}>
                              {formatAiBudgetNumber(aiBudgetSnapshot.basicRequests)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">{t('adminAiBudgetBasicRequestsHint')}</p>
                          </div>
                          <div className="rounded-md bg-stone-50 p-4">
                            <p className="text-xs font-medium uppercase text-slate-500">{t('adminAiBudgetEstimatedCost')}</p>
                            <p className={`mt-2 text-2xl font-semibold ${aiBudgetSnapshot.isOverBudget ? 'text-red-600' : 'text-slate-900'}`}>
                              {formatAiBudgetUsd(aiBudgetSnapshot.estimatedCostUsd)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {t('adminAiBudgetGlobalBudget', { amount: formatAiBudgetUsd(aiBudgetSnapshot.dailyBudgetUsd) })}
                            </p>
                          </div>
                          <div className="rounded-md bg-stone-50 p-4">
                            <p className="text-xs font-medium uppercase text-slate-500">{t('adminAiBudgetOverBudgetUsers')}</p>
                            <p className={`mt-2 text-2xl font-semibold ${(aiBudgetSnapshot.overBudgetUsers ?? 0) > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                              {formatAiBudgetNumber(aiBudgetSnapshot.overBudgetUsers)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">{t('adminAiBudgetOverBudgetUsersHint')}</p>
                          </div>
                        </div>

                        <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                          {t('adminAiBudgetRuntimeNotice')}
                        </div>

                        <div className="grid gap-6 xl:grid-cols-2">
                          <div className="space-y-3">
                            <div>
                              <h2 className="text-sm font-semibold text-slate-800">{t('adminAiBudgetContextsTitle')}</h2>
                              <p className="text-xs text-slate-500">{t('adminAiBudgetContextsDescription')}</p>
                            </div>
                            <div className="overflow-x-auto rounded-md border border-stone-200">
                              <Table>
                                <TableHeader>
                                  <TableHead>{t('adminTableContext')}</TableHead>
                                  <TableHead>{t('adminAiBudgetPlanTier')}</TableHead>
                                  <TableHead>{t('adminAiBudgetRequests')}</TableHead>
                                  <TableHead>{t('adminAiBudgetTokens')}</TableHead>
                                  <TableHead>{t('adminAiBudgetCost')}</TableHead>
                                </TableHeader>
                                <TableBody>
                                  {aiBudgetContexts.map((context) => (
                                    <TableRow key={`${context.context ?? '-'}-${context.planTier ?? '-'}`}>
                                      <TableCell className="font-medium text-slate-800">{context.context || t('adminNotAvailable')}</TableCell>
                                      <TableCell>{getPlanTierLabel(context.planTier)}</TableCell>
                                      <TableCell>{formatAiBudgetNumber(context.requests)}</TableCell>
                                      <TableCell>{formatAiBudgetNumber(context.estimatedTokens)}</TableCell>
                                      <TableCell>{formatAiBudgetUsd(context.estimatedCostUsd)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                            {aiBudgetContexts.length === 0 && (
                              <p className="text-sm text-slate-500">{t('adminAiBudgetNoContextRows')}</p>
                            )}
                          </div>

                          <div className="space-y-3">
                            <div>
                              <h2 className="text-sm font-semibold text-slate-800">{t('adminAiBudgetUsersTitle')}</h2>
                              <p className="text-xs text-slate-500">{t('adminAiBudgetUsersDescription')}</p>
                            </div>
                            <div className="overflow-x-auto rounded-md border border-stone-200">
                              <Table>
                                <TableHeader>
                                  <TableHead>{t('adminAiBudgetUserHash')}</TableHead>
                                  <TableHead>{t('adminAiBudgetPlanTier')}</TableHead>
                                  <TableHead>{t('adminAiBudgetRequests')}</TableHead>
                                  <TableHead>{t('adminAiBudgetCost')}</TableHead>
                                  <TableHead>{t('adminAiBudgetUserBudget')}</TableHead>
                                  <TableHead>{t('adminTableStatus')}</TableHead>
                                </TableHeader>
                                <TableBody>
                                  {aiBudgetUsers.map((user) => (
                                    <TableRow key={`${user.userIdHash ?? '-'}-${user.planTier ?? '-'}`}>
                                      <TableCell className="max-w-[180px] truncate font-mono text-xs text-slate-700" title={user.userIdHash ?? undefined}>
                                        {user.userIdHash || t('adminNotAvailable')}
                                      </TableCell>
                                      <TableCell>{getPlanTierLabel(user.planTier)}</TableCell>
                                      <TableCell>{formatAiBudgetNumber(user.requests)}</TableCell>
                                      <TableCell>{formatAiBudgetUsd(user.estimatedCostUsd)}</TableCell>
                                      <TableCell>
                                        {user.hasDailyBudget
                                          ? formatAiBudgetUsd(user.dailyBudgetUsd)
                                          : t('adminNotAvailable')}
                                      </TableCell>
                                      <TableCell>
                                        <Badge
                                          variant={user.isOverBudget ? 'destructive' : 'outline'}
                                          className={user.isOverBudget ? '' : 'border-emerald-200 text-emerald-700'}
                                        >
                                          {user.isOverBudget ? t('adminAiBudgetStatusOver') : t('adminAiBudgetStatusWithin')}
                                        </Badge>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                            {aiBudgetUsers.length === 0 && (
                              <p className="text-sm text-slate-500">{t('adminAiBudgetNoUserRows')}</p>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="failed-jobs">
                <Card className="bg-white border-stone-200 shadow-lg">
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <CardTitle className="text-slate-800 flex items-center text-lg">
                          <FileWarning className="h-5 w-5 mr-2 text-red-500" />
                          {t('adminTabFailedJobs')}
                        </CardTitle>
                        <p className="text-sm text-slate-500 mt-1">
                          {t('adminFailedJobsDescription')}
                        </p>
                      </div>
                      <div className="relative w-full md:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder={t('adminSearchJobs')}
                          className="pl-9"
                          value={jobSearchTerm}
                          onChange={(e) => { setJobSearchTerm(e.target.value); setJobsPage(1) }}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableHead>ID</TableHead>
                          <TableHead>{t('adminTableContext')}</TableHead>
                          <TableHead>{t('treatmentPreference')}</TableHead>
                          <TableHead>{t('adminTableProfileKey')}</TableHead>
                          <TableHead>{t('adminTableDatePeriod')}</TableHead>
                          <TableHead>{t('adminTableSchema')}</TableHead>
                          <TableHead>{t('adminTableStatus')}</TableHead>
                          <TableHead>{t('adminTableAttempts')}</TableHead>
                          <TableHead>{t('adminTableCreatedAt')}</TableHead>
                          <TableHead className="text-right">{t('adminTableActions')}</TableHead>
                        </TableHeader>
                        <TableBody>
                          {paginatedJobs.map((job) => (
                            <TableRow key={job.id} className="hover:bg-stone-50">
                              <TableCell className="font-mono text-xs text-slate-600 max-w-[100px] truncate" title={job.id}>
                                {job.id.slice(0, 8)}...
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={`text-xs ${job.context === "DailyAstro" ? "border-blue-300 text-blue-700" :
                                  job.context === "ProfileHighlightSummary" ? "border-purple-300 text-purple-700" :
                                    job.context === "ProfileSummary" ? "border-amber-300 text-amber-700" :
                                      "border-emerald-300 text-emerald-700"
                                  }`}>
                                  {job.context}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-slate-700">
                                {getTreatmentPreferenceLabel(job.treatmentPreference)}
                              </TableCell>
                              <TableCell className="text-xs text-slate-600 max-w-[120px] truncate" title={job.profileKey || "-"}>
                                {job.profileKey || <span className="text-slate-400">-</span>}
                              </TableCell>
                              <TableCell className="text-xs text-slate-700">
                                {job.date ? (
                                  new Date(job.date).toLocaleDateString("pt-PT")
                                ) : job.startDay && job.endDay ? (
                                  <span>{new Date(job.startDay).toLocaleDateString("pt-PT")} - {new Date(job.endDay).toLocaleDateString("pt-PT")}</span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="font-mono text-xs text-slate-500">{job.schemaVersion}</span>
                              </TableCell>
                              <TableCell>
                                <Badge className={`text-xs ${job.status === "Failed" ? "bg-red-500" :
                                  job.status === "Processing" ? "bg-blue-500" :
                                    job.status === "Completed" ? "bg-emerald-500" :
                                      job.status === "Canceled" ? "bg-slate-500" :
                                        "bg-amber-500"
                                  }`}>
                                  {getJobStatusLabel(job.status)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <span className={`text-sm font-semibold ${job.attempts >= 3 ? "text-red-600" : "text-slate-700"}`}>
                                  {job.attempts}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs text-slate-600">
                                {new Date(job.createdAtUtc).toLocaleDateString("pt-PT")}{" "}
                                <span className="text-slate-400">{new Date(job.createdAtUtc).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSelectedJob(job); setIsJobDetailOpen(true) }}
                                    className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
                                    title={t('adminViewDetails')}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSelectedJob(job); setIsRetryDialogOpen(true) }}
                                    disabled={retryingJobId === job.id || cancelingJobId === job.id || job.status === "Processing" || job.status === "Canceled"}
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                    title={t('adminRetryJob')}
                                  >
                                    {retryingJobId === job.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <RefreshCw className="h-4 w-4" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSelectedJob(job); setIsCancelJobDialogOpen(true) }}
                                    disabled={cancelingJobId === job.id || retryingJobId === job.id || job.status === "Processing" || job.status === "Canceled"}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title={t('adminCancelJob')}
                                  >
                                    {cancelingJobId === job.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <X className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {filteredJobs.length === 0 && (
                      <div className="text-center py-12">
                        <FileWarning className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">{t('adminNoFailedJobsFound')}</p>
                      </div>
                    )}
                    {filteredJobs.length > 0 && (
                      <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-stone-200 mt-4 gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">{t('adminShow')}</span>
                          <Select
                            value={String(jobsPerPage)}
                            onValueChange={(val) => {
                              setJobsPerPage(Number(val))
                              setJobsPage(1)
                            }}
                          >
                            <SelectTrigger className="w-[70px] h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="20">20</SelectItem>
                              <SelectItem value="40">40</SelectItem>
                            </SelectContent>
                          </Select>
                          <span className="text-sm text-slate-600">{t('adminPerPage')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            {t('adminPaginationRange', {
                              start: (jobsPage - 1) * jobsPerPage + 1,
                              end: Math.min(jobsPage * jobsPerPage, filteredJobs.length),
                              total: filteredJobs.length,
                            })}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={jobsPage <= 1}
                            onClick={() => setJobsPage((p) => p - 1)}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm font-medium text-slate-700">
                            {jobsPage} / {totalJobsPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={jobsPage >= totalJobsPages}
                            onClick={() => setJobsPage((p) => p + 1)}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </main>

          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogContent className="inset-0 left-0 top-0 h-[100dvh] max-h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden overscroll-none rounded-none border-0 bg-white p-0 shadow-none sm:inset-auto sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[85vh] sm:w-full sm:max-w-[500px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:gap-4 sm:rounded-lg sm:border sm:bg-white sm:p-6 sm:shadow-lg [&>button]:hidden sm:[&>button]:inline-flex">
              <div className="flex h-full min-h-0 flex-col">
                <DialogHeader className="shrink-0 border-b border-slate-200 px-5 pb-4 pt-[calc(var(--app-safe-top)+1rem)] text-left sm:border-b-0 sm:px-0 sm:pb-0 sm:pt-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1.5 pr-4">
                      <DialogTitle>{t('adminCreateNewUser')}</DialogTitle>
                      <DialogDescription>
                        {t('adminCreateUserDescription')}
                      </DialogDescription>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-10 w-10 shrink-0 p-0 text-slate-500 hover:bg-slate-100 sm:hidden"
                      onClick={() => {
                        setIsCreateDialogOpen(false);
                        resetForm();
                      }}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-5 py-5 [-webkit-overflow-scrolling:touch] sm:px-0 sm:py-4">
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t('adminFullName')}</Label>
                      <Input
                        id="name"
                        placeholder={t('adminFullNamePlaceholder')}
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">{t('email')}</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder={t('adminEmailPlaceholder')}
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                      />
                    </div>

                    {/* Operator screen: never offer the operator's own birthday. */}
                    <BirthDateInput
                      id="birthDate"
                      max={today}
                      autoComplete="off"
                      showErrors={showBirthErrors}
                      value={formData.birthDate}
                      onValueChange={(draft) =>
                        setFormData({ ...formData, birthDate: draft })
                      }
                      error={showBirthErrors && isBirthPairPartial
                        ? t('adminBirthPairIncomplete')
                        : undefined}
                    />

                    <TimeInput
                      id="birthTime"
                      showErrors={showBirthErrors}
                      value={formData.birthTime}
                      onValueChange={(draft) =>
                        setFormData({ ...formData, birthTime: draft })
                      }
                      error={showBirthErrors && isBirthPairPartial
                        ? t('adminBirthPairIncomplete')
                        : undefined}
                    />

                    {renderBirthPlaceField("birthPlace")}

                    <div className="space-y-2">
                      <Label htmlFor="language">{t('adminPreferredLanguage')}</Label>
                      <Select
                        value={formData.language}
                        onValueChange={(value: (typeof LANGUAGE_OPTIONS)[number]['value']) =>
                          setFormData({ ...formData, language: value })
                        }
                      >
                        <SelectTrigger id="language">
                          <SelectValue placeholder={t('adminSelectLanguage')} />
                        </SelectTrigger>
                        <SelectContent className='bg-white'>
                          {translatedLanguageOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="treatmentPreference">{t('treatmentPreference')}</Label>
                      <Select
                        value={formData.treatmentPreference}
                        onValueChange={(value: UserTreatmentPreference) =>
                          setFormData({ ...formData, treatmentPreference: value })
                        }
                      >
                        <SelectTrigger id="treatmentPreference">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className='bg-white'>
                          {translatedTreatmentPreferenceOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="role">{t('adminAccountType')}</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(value: 'user' | 'admin') =>
                          setFormData({ ...formData, role: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className='bg-white'>
                          <SelectItem value="user">{t('adminRoleUser')}</SelectItem>
                          <SelectItem value="admin">{t('adminRoleAdmin')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <DialogFooter className="shrink-0 gap-3 border-t border-slate-200 bg-white px-5 pb-[calc(var(--app-safe-bottom)+1rem)] pt-4 sm:border-t-0 sm:px-0 sm:pb-0 sm:pt-0">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      setIsCreateDialogOpen(false);
                      resetForm();
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handleCreateUser}
                    className="w-full bg-action-700 hover:bg-action-800 sm:w-auto"
                  >
                    {t('adminCreateUser')}
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="inset-0 left-0 top-0 h-[100dvh] max-h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden overscroll-none rounded-none border-0 bg-white p-0 shadow-none sm:inset-auto sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[85vh] sm:w-full sm:max-w-[500px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:gap-4 sm:rounded-lg sm:border sm:bg-white sm:p-6 sm:shadow-lg [&>button]:hidden sm:[&>button]:inline-flex">
              <div className="flex h-full min-h-0 flex-col">
                <DialogHeader className="shrink-0 border-b border-slate-200 px-5 pb-4 pt-[calc(var(--app-safe-top)+1rem)] text-left sm:border-b-0 sm:px-0 sm:pb-0 sm:pt-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1.5 pr-4">
                      <DialogTitle>{t('adminEditUser')}</DialogTitle>
                      <DialogDescription>
                        {t('adminEditUserDescription')}
                      </DialogDescription>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-10 w-10 shrink-0 p-0 text-slate-500 hover:bg-slate-100 sm:hidden"
                      onClick={() => {
                        setIsEditDialogOpen(false);
                        resetForm();
                      }}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-5 py-5 [-webkit-overflow-scrolling:touch] sm:px-0 sm:py-4">
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="edit-name">{t('adminFullName')}</Label>
                      <Input
                        id="edit-name"
                        placeholder={t('adminFullNamePlaceholder')}
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-email">{t('email')}</Label>
                      <Input
                        id="edit-email"
                        type="email"
                        placeholder={t('adminEmailPlaceholder')}
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                      />
                    </div>

                    {/* The update endpoint has no empty birth instant, so both are required. */}
                    <BirthDateInput
                      id="edit-birthDate"
                      required
                      max={today}
                      autoComplete="off"
                      showErrors={showBirthErrors}
                      value={formData.birthDate}
                      onValueChange={(draft) =>
                        setFormData({ ...formData, birthDate: draft })
                      }
                    />

                    <TimeInput
                      id="edit-birthTime"
                      required
                      showErrors={showBirthErrors}
                      value={formData.birthTime}
                      onValueChange={(draft) =>
                        setFormData({ ...formData, birthTime: draft })
                      }
                    />

                    {renderBirthPlaceField("edit-birthPlace")}

                    <div className="space-y-2">
                      <Label htmlFor="edit-language">{t('adminPreferredLanguage')}</Label>
                      <Select
                        value={formData.language}
                        onValueChange={(value: (typeof LANGUAGE_OPTIONS)[number]['value']) =>
                          setFormData({ ...formData, language: value })
                        }
                      >
                        <SelectTrigger id="edit-language">
                          <SelectValue placeholder={t('adminSelectLanguage')} />
                        </SelectTrigger>
                        <SelectContent className='bg-white'>
                          {translatedLanguageOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-treatmentPreference">{t('treatmentPreference')}</Label>
                      <Select
                        value={formData.treatmentPreference}
                        onValueChange={(value: UserTreatmentPreference) =>
                          setFormData({ ...formData, treatmentPreference: value })
                        }
                      >
                        <SelectTrigger id="edit-treatmentPreference">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className='bg-white'>
                          {translatedTreatmentPreferenceOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="edit-role">{t('adminAccountType')}</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(value: 'user' | 'admin') =>
                          setFormData({ ...formData, role: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className='bg-white'>
                          <SelectItem value="user">{t('adminRoleUser')}</SelectItem>
                          <SelectItem value="admin">{t('adminRoleAdmin')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <DialogFooter className="shrink-0 gap-3 border-t border-slate-200 bg-white px-5 pb-[calc(var(--app-safe-bottom)+1rem)] pt-4 sm:border-t-0 sm:px-0 sm:pb-0 sm:pt-0">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      setIsEditDialogOpen(false);
                      resetForm();
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handleEditUser}
                    className="w-full bg-blue-500 hover:bg-blue-600 sm:w-auto"
                  >
                    {t('adminSaveChanges')}
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <AlertDialogContent className='bg-white'>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('adminConfirmDeleteTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('adminDeleteUserDescriptionPrefix')} <strong>{selectedUser?.name}</strong> {t('adminDeleteUserDescriptionSuffix')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700 focus:ring-red-600">
                  {t('adminRemove')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={isTerminateSessionsDialogOpen} onOpenChange={setIsTerminateSessionsDialogOpen}>
            <AlertDialogContent className='bg-white'>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('adminConfirmTerminateSessionsTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('adminTerminateSessionsDescriptionPrefix')} <strong>{selectedUser?.name}</strong> {t('adminTerminateSessionsDescriptionSuffix')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleTerminateSessions}
                  className="bg-orange-600 hover:bg-orange-700 focus:ring-orange-600"
                >
                  {t('adminTerminateSessionsAction')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Dialog open={isCreateSubscriptionDialogOpen} onOpenChange={setIsCreateSubscriptionDialogOpen}>
            <DialogContent className="sm:max-w-[500px] bg-white">
              <DialogHeader>
                <DialogTitle>{t('adminCreateNewSubscription')}</DialogTitle>
                <DialogDescription>{t('adminCreateSubscriptionDescription')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="sub-user">{t('adminTableUser')}</Label>
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="sub-user"
                        placeholder={t('adminSearchByName')}
                        value={subscriptionFormData.userId ? (users.find((u) => u.id === subscriptionFormData.userId)?.name || createSubUserSearch) : createSubUserSearch}
                        onChange={(e) => {
                          setCreateSubUserSearch(e.target.value)
                          setIsUserDropdownOpen(true)
                          if (subscriptionFormData.userId) {
                            setSubscriptionFormData({ ...subscriptionFormData, userId: "" })
                          }
                        }}
                        onFocus={() => setIsUserDropdownOpen(true)}
                        className="pl-9"
                      />
                    </div>
                    {isUserDropdownOpen && !subscriptionFormData.userId && (
                      <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-stone-200 bg-white shadow-lg">
                        {users.filter((u) =>
                          u.name.toLowerCase().includes(createSubUserSearch.toLowerCase())
                        ).length === 0 ? (
                          <div className="px-3 py-4 text-center text-sm text-slate-500">
                            {t('adminNoUsersFound')}
                          </div>
                        ) : (
                          users.filter((u) =>
                            u.name.toLowerCase().includes(createSubUserSearch.toLowerCase())
                          ).map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-stone-50 transition-colors border-b border-stone-100 last:border-0"
                              onClick={() => {
                                setSubscriptionFormData({ ...subscriptionFormData, userId: user.id })
                                setCreateSubUserSearch("")
                                setIsUserDropdownOpen(false)
                              }}
                            >
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-action-100 text-action-800 text-xs font-bold">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{user.name}</p>
                                <p className="text-xs text-slate-500 truncate">{user.email}</p>
                              </div>
                              <Badge variant="outline" className="text-xs shrink-0 border-slate-200">
                                {getUserRoleLabel(user.role)}
                              </Badge>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                    {subscriptionFormData.userId && (
                      <div className="mt-2 flex items-center gap-2 p-2 bg-action-50 border border-action-200 rounded-md">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-action-100 text-action-800 text-xs font-bold">
                          {users.find((u) => u.id === subscriptionFormData.userId)?.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {users.find((u) => u.id === subscriptionFormData.userId)?.name}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {users.find((u) => u.id === subscriptionFormData.userId)?.email}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                          onClick={() => {
                            setSubscriptionFormData({ ...subscriptionFormData, userId: "" })
                            setCreateSubUserSearch("")
                          }}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sub-plan">{t('adminTablePlan')}</Label>
                  <Select
                    value={subscriptionFormData.planId}
                    onValueChange={(value) =>
                      setSubscriptionFormData({ ...subscriptionFormData, planId: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('adminSelectPlan')} />
                    </SelectTrigger>
                    <SelectContent className='bg-white'>
                      {subscriptionPlans.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-slate-500">{t('adminNoPlansAvailable')}</div>
                      ) : (
                        subscriptionPlans.map((plan) => (
                          <SelectItem key={plan.code ?? plan.name ?? ''} value={plan.code ?? ''}>
                            {plan.name ?? plan.code} - {(plan.priceIncludingTax ?? plan.priceExcludingTax)?.toFixed(2)} {plan.currency}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateSubscriptionDialogOpen(false)
                    resetSubscriptionForm()
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button onClick={handleCreateSubscription} className="bg-action-700 hover:bg-action-800">
                  {t('adminCreateSubscription')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isViewSubscriptionDialogOpen} onOpenChange={setIsViewSubscriptionDialogOpen}>
            <DialogContent className="inset-0 left-0 top-0 h-[100dvh] max-h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden overscroll-none rounded-none border-0 bg-white p-0 shadow-none sm:inset-auto sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[85vh] sm:w-full sm:max-w-[580px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:gap-4 sm:rounded-lg sm:border sm:bg-white sm:p-6 sm:shadow-lg [&>button]:hidden sm:[&>button]:inline-flex">
              <div className="flex h-full min-h-0 flex-col">
                <DialogHeader className="shrink-0 border-b border-slate-200 px-5 pb-4 pt-[calc(var(--app-safe-top)+1rem)] text-left sm:border-b-0 sm:px-0 sm:pb-0 sm:pt-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1.5 pr-4">
                      <DialogTitle className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-slate-500" />
                        {t('adminSubscriptionDetails')}
                      </DialogTitle>
                      <DialogDescription>
                        {t('adminSubscriptionOf')} <strong>{selectedSubscription?.user?.name}</strong>
                      </DialogDescription>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-10 w-10 shrink-0 p-0 text-slate-500 hover:bg-slate-100 sm:hidden"
                      onClick={() => setIsViewSubscriptionDialogOpen(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </DialogHeader>
                {selectedSubscription && (
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-5 py-5 [-webkit-overflow-scrolling:touch] sm:px-0 sm:py-4">
                    <div className="space-y-4">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('adminTableUser')}</p>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                          <div>
                            <span className="text-slate-500">{t('name')}:</span>
                            <span className="ml-1 font-semibold text-slate-800">{selectedSubscription.user?.name}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminPublicId')}:</span>
                            <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedSubscription.user?.publicId}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('language')}:</span>
                            <span className="ml-1 text-slate-700">{selectedSubscription.user?.language?.toUpperCase()}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminTimezone')}:</span>
                            <span className="ml-1 text-slate-700">{selectedSubscription.user?.tzId}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminType')}:</span>
                            <span className="ml-1 text-slate-700">{selectedSubscription.user?.type}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminBanned')}:</span>
                            <span className={`ml-1 font-medium ${selectedSubscription.user?.isBanned === true ? "text-red-600" : "text-emerald-600"}`}>
                              {selectedSubscription.user?.isBanned === true ? t('adminYes') : t('adminNo')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-purple-600">{t('adminTablePlan')}</p>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                          <div>
                            <span className="text-slate-500">{t('name')}:</span>
                            <span className="ml-1 font-semibold text-slate-800">{selectedSubscription.plan?.name}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminTier')}:</span>
                            <Badge variant="outline" className={`ml-1 text-xs ${selectedSubscription.plan?.tier === SubscriptionTier.Pro ? "border-purple-300 text-purple-700" :
                              selectedSubscription.plan?.tier === SubscriptionTier.Basic ? "border-blue-300 text-blue-700" :
                                "border-slate-300 text-slate-600"
                              }`}>
                              {selectedSubscription.plan?.tier}
                            </Badge>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminPrice')}:</span>
                            <span className="ml-1 font-bold text-slate-800">{selectedSubscription.plan?.price?.toFixed(2)} {selectedSubscription.plan?.currency}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminInterval')}:</span>
                            <span className="ml-1 text-slate-700">
                              {getSubscriptionIntervalLabel(selectedSubscription.plan?.interval)}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminCode')}:</span>
                            <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedSubscription.plan?.code}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminStatusActive')}:</span>
                            <span className={`ml-1 font-medium ${selectedSubscription.plan?.isActive === true ? "text-emerald-600" : "text-red-600"}`}>
                              {selectedSubscription.plan?.isActive === true ? t('adminYes') : t('adminNo')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className={`rounded-lg border p-4 ${selectedSubscription.status === UserSubscriptionStatus.Active || selectedSubscription.status === UserSubscriptionStatus.Trial ? "bg-emerald-50 border-emerald-200" :
                        selectedSubscription.status === UserSubscriptionStatus.Canceled ? "bg-orange-50 border-orange-200" : "bg-red-50 border-red-200"
                        }`}>
                        <p className={`mb-3 text-xs font-semibold uppercase tracking-wide ${selectedSubscription.status === UserSubscriptionStatus.Active || selectedSubscription.status === UserSubscriptionStatus.Trial ? "text-emerald-600" :
                          selectedSubscription.status === UserSubscriptionStatus.Canceled ? "text-orange-600" : "text-red-600"
                          }`}>{t('adminTabSubscriptions')}</p>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                          <div>
                            <span className="text-slate-500">ID:</span>
                            <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedSubscription.id?.slice(0, 16)}...</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminTableStatus')}:</span>
                            <Badge className={`ml-1 text-xs ${selectedSubscription.status === UserSubscriptionStatus.Active || selectedSubscription.status === UserSubscriptionStatus.Trial ? "bg-emerald-500" :
                              selectedSubscription.status === UserSubscriptionStatus.Canceled ? "bg-orange-500" : "bg-red-500"
                              }`}>
                              {getSubscriptionStatusLabel(selectedSubscription.status)}
                            </Badge>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminTableStartDate')}:</span>
                            <span className="ml-1 font-semibold text-slate-800">{selectedSubscription.startsAtUtc?.toLocaleDateString("pt-PT")}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminTableEndDate')}:</span>
                            <span className="ml-1 font-semibold text-slate-800">{selectedSubscription.endsAtUtc?.toLocaleDateString("pt-PT")}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminCreatedOn')}:</span>
                            <span className="ml-1 text-slate-700">{selectedSubscription.createdAtUtc?.toLocaleDateString("pt-PT")}</span>
                          </div>
                          {selectedSubscription.updatedAtUtc && (
                            <div>
                              <span className="text-slate-500">{t('adminUpdatedOn')}:</span>
                              <span className="ml-1 text-slate-700">{selectedSubscription.updatedAtUtc?.toLocaleDateString("pt-PT")}</span>
                            </div>
                          )}
                          {selectedSubscription.canceledAtUtc && (
                            <div className="sm:col-span-2">
                              <span className="text-slate-500">{t('adminCanceledOn')}:</span>
                              <span className="ml-1 font-semibold text-red-600">{selectedSubscription.canceledAtUtc?.toLocaleDateString("pt-PT")}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('adminPayment')}</p>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                          <div>
                            <span className="text-slate-500">{t('adminOrderId')}:</span>
                            <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedSubscription.sourceOrderId}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminPaymentId')}:</span>
                            <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedSubscription.lastPaymentId}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <DialogFooter className="shrink-0 border-t border-slate-200 bg-white px-5 pb-[calc(var(--app-safe-bottom)+1rem)] pt-4 sm:border-t-0 sm:px-0 sm:pb-0 sm:pt-0">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => setIsViewSubscriptionDialogOpen(false)}
                  >
                    {t('close')}
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isRefundRequestDetailOpen} onOpenChange={setIsRefundRequestDetailOpen}>
            <DialogContent className="sm:max-w-[720px] bg-white">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <LifeBuoy className="h-5 w-5 text-amber-500" />
                  {t('adminRefundRequestDetailTitle')}
                </DialogTitle>
                <DialogDescription>
                  {t('adminRefundRequestDetailDescription')}
                </DialogDescription>
              </DialogHeader>
              {selectedRefundRequest && (
                <div className="space-y-4 py-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('adminTableUser')}</p>
                    <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                      <div>
                        <span className="text-slate-500">{t('name')}:</span>
                        <span className="ml-1 font-semibold text-slate-800">{selectedRefundRequest.userName}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">{t('adminPublicId')}:</span>
                        <span className="ml-1 text-slate-800">{selectedRefundRequest.userPublicId ? `@${selectedRefundRequest.userPublicId}` : t('adminNotAvailable')}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">{t('adminTablePlan')}:</span>
                        <span className="ml-1 text-slate-800">{selectedRefundRequest.planName || t('adminNotAvailable')}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">{t('adminRefundRequestContact')}:</span>
                        <span className="ml-1 text-slate-800">{selectedRefundRequest.contactEmail}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('adminRefundRequestRequestData')}</p>
                    <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                      <div>
                        <span className="text-slate-500">{t('adminTableCreatedAt')}:</span>
                        <span className="ml-1 text-slate-800">{new Date(selectedRefundRequest.createdAtUtc).toLocaleString("pt-PT")}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">{t('adminRefundRequestEligibleUntil')}:</span>
                        <span className="ml-1 text-slate-800">{new Date(selectedRefundRequest.eligibleUntilUtc).toLocaleString("pt-PT")}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">{t('adminTableStatus')}:</span>
                        <Badge
                          variant={
                            selectedRefundRequest.status === 'Processed'
                              ? 'secondary'
                              : selectedRefundRequest.status === 'Rejected'
                                ? 'destructive'
                                : 'outline'
                          }
                          className={selectedRefundRequest.status === 'Processed' ? 'ml-1 bg-emerald-100 text-emerald-800' : 'ml-1'}
                        >
                          {getRefundRequestStatusLabel(selectedRefundRequest.status)}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-slate-500">{t('adminOrderId')}:</span>
                        <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedRefundRequest.orderId || t('adminNotAvailable')}</span>
                      </div>
                      <div className="sm:col-span-2">
                        <span className="text-slate-500">{t('adminPaymentId')}:</span>
                        <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedRefundRequest.paymentId || t('adminNotAvailable')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('adminRefundRequestReason')}</p>
                    <p className="whitespace-pre-wrap text-sm leading-6 text-slate-800">{selectedRefundRequest.reason}</p>
                    {selectedRefundRequest.adminNotes ? (
                      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        {selectedRefundRequest.adminNotes}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
              <DialogFooter>
                {selectedRefundRequest?.status === 'Pending' ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => selectedRefundRequest && handleRefundRequestStatusUpdate(selectedRefundRequest, 'Rejected')}
                      disabled={pendingRefundStatus !== null}
                    >
                      {pendingRefundStatus === 'Rejected' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <X className="h-4 w-4" />
                      )}
                      {t('adminRefundRequestReject')}
                    </Button>
                    <Button
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={() => selectedRefundRequest && handleRefundRequestStatusUpdate(selectedRefundRequest, 'Processed')}
                      disabled={pendingRefundStatus !== null}
                    >
                      {pendingRefundStatus === 'Processed' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      {t('adminRefundRequestMarkProcessed')}
                    </Button>
                  </>
                ) : null}
                <Button
                  variant="outline"
                  onClick={() => setIsRefundRequestDetailOpen(false)}
                >
                  {t('close')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <AlertDialog open={isCancelSubscriptionDialogOpen} onOpenChange={setIsCancelSubscriptionDialogOpen}>
            <AlertDialogContent className='bg-white'>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('adminCancelSubscriptionDialogTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('adminCancelSubscriptionDialogDescription')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('adminKeepSubscription')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleCancelSubscription} className="bg-red-500 hover:bg-red-600">
                  {t('adminCancelNow')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={isRetryDialogOpen} onOpenChange={setIsRetryDialogOpen}>
            <AlertDialogContent className='bg-white'>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-amber-500" />
                  {t('adminRetryJobTitle')}
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-3">
                  <span>{t('adminRetryJobDescription')}</span>
                  {selectedJob && (
                    <span className="block mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm">
                      <span className="block"><strong>{t('adminTableContext')}:</strong> {selectedJob.context}</span>
                      <span className="block"><strong>{t('adminTableProfileKey')}:</strong> {selectedJob.profileKey || t('adminNotAvailable')}</span>
                      <span className="block"><strong>{t('adminPreviousAttempts')}:</strong> {selectedJob.attempts}</span>
                      {selectedJob.error && (
                        <span className="block mt-1 text-red-600 text-xs"><strong>{t('adminLastError')}:</strong> {selectedJob.error}</span>
                      )}
                    </span>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={!!retryingJobId}>{t('cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => selectedJob && handleRetryJob(selectedJob.id)}
                  className="bg-amber-500 hover:bg-amber-600"
                  disabled={!!retryingJobId}
                >
                  {retryingJobId ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('adminRetrying')}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {t('adminRetryJob')}
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={isCancelJobDialogOpen} onOpenChange={setIsCancelJobDialogOpen}>
            <AlertDialogContent className='bg-white'>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <X className="h-5 w-5 text-red-500" />
                  {t('adminCancelJobTitle')}
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-3">
                  <span>{t('adminCancelJobDescription')}</span>
                  {selectedJob && (
                    <span className="block mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm">
                      <span className="block"><strong>{t('adminTableContext')}:</strong> {selectedJob.context}</span>
                      <span className="block"><strong>{t('adminTableProfileKey')}:</strong> {selectedJob.profileKey || t('adminNotAvailable')}</span>
                    </span>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={!!cancelingJobId}>{t('goBack')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => selectedJob && handleCancelJob(selectedJob.id)}
                  className="bg-red-500 hover:bg-red-600 focus:ring-red-600"
                  disabled={!!cancelingJobId}
                >
                  {cancelingJobId ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('adminCanceling')}
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-2" />
                      {t('adminCancelPermanently')}
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Dialog open={isJobDetailOpen} onOpenChange={setIsJobDetailOpen}>
            <DialogContent className="inset-0 left-0 top-0 h-[100dvh] max-h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden overscroll-none rounded-none border-0 bg-white p-0 shadow-none sm:inset-auto sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[85vh] sm:w-full sm:max-w-[600px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:gap-4 sm:rounded-lg sm:border sm:bg-white sm:p-6 sm:shadow-lg [&>button]:hidden sm:[&>button]:inline-flex">
              <div className="flex h-full min-h-0 flex-col">
                <DialogHeader className="shrink-0 border-b border-slate-200 px-5 pb-4 pt-[calc(var(--app-safe-top)+1rem)] text-left sm:border-b-0 sm:px-0 sm:pb-0 sm:pt-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1.5 pr-4">
                      <DialogTitle className="flex items-center gap-2">
                        <FileWarning className="h-5 w-5 text-red-500" />
                        {t('adminJobDetails')}
                      </DialogTitle>
                      <DialogDescription>
                        {t('adminJobDetailsDescription')}
                      </DialogDescription>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-10 w-10 shrink-0 p-0 text-slate-500 hover:bg-slate-100 sm:hidden"
                      onClick={() => setIsJobDetailOpen(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </DialogHeader>
                {selectedJob && (
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-5 py-5 [-webkit-overflow-scrolling:touch] sm:px-0 sm:py-4">
                    <div className="space-y-4">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('adminIdentification')}</p>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <span className="text-slate-500">{t('adminJobId')}:</span>
                            <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedJob.id}</span>
                          </div>
                          <div className="sm:col-span-2">
                            <span className="text-slate-500">{t('adminNatalChartId')}:</span>
                            <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedJob.natalChartId}</span>
                          </div>
                          {selectedJob.batchId && (
                            <div className="sm:col-span-2">
                              <span className="text-slate-500">{t('adminBatchId')}:</span>
                              <span className="ml-1 break-all font-mono text-xs text-slate-700">{selectedJob.batchId}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-blue-600">{t('adminConfiguration')}</p>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                          <div>
                            <span className="text-slate-500">{t('adminTableContext')}:</span>
                            <Badge variant="outline" className="ml-1 text-xs">{selectedJob.context}</Badge>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminTableProfileKey')}:</span>
                            <span className="ml-1 text-slate-700">{selectedJob.profileKey || <span className="text-slate-400">{t('adminNotAvailable')}</span>}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('language')}:</span>
                            <span className="ml-1 font-medium text-slate-700">{selectedJob.language.toUpperCase()}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('treatmentPreference')}:</span>
                            <span className="ml-1 text-slate-700">{getTreatmentPreferenceLabel(selectedJob.treatmentPreference)}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminTableSchema')}:</span>
                            <span className="ml-1 font-mono text-xs text-slate-700">v{selectedJob.schemaVersion}</span>
                          </div>
                          {selectedJob.date && (
                            <div>
                              <span className="text-slate-500">{t('adminDate')}:</span>
                              <span className="ml-1 text-slate-700">{new Date(selectedJob.date).toLocaleDateString("pt-PT")}</span>
                            </div>
                          )}
                          {selectedJob.startDay && selectedJob.endDay && (
                            <div className="sm:col-span-2">
                              <span className="text-slate-500">{t('adminPeriod')}:</span>
                              <span className="ml-1 text-slate-700">
                                {new Date(selectedJob.startDay).toLocaleDateString("pt-PT")} - {new Date(selectedJob.endDay).toLocaleDateString("pt-PT")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-red-600">{t('adminStateAndError')}</p>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                          <div>
                            <span className="text-slate-500">{t('adminTableStatus')}:</span>
                            <Badge className={`ml-1 text-xs ${selectedJob.status === "Failed" ? "bg-red-500" :
                              selectedJob.status === "Processing" ? "bg-blue-500" :
                                "bg-emerald-500"
                              }`}>
                              {getJobStatusLabel(selectedJob.status)}
                            </Badge>
                          </div>
                          <div>
                            <span className="text-slate-500">{t('adminTableAttempts')}:</span>
                            <span className={`ml-1 font-bold ${selectedJob.attempts >= 3 ? "text-red-600" : "text-slate-700"}`}>{selectedJob.attempts}</span>
                          </div>
                        </div>
                        {selectedJob.error && (
                          <div className="mt-3 rounded-md border border-red-200 bg-red-100 p-3">
                            <p className="mb-1 text-xs font-semibold text-red-700">{t('adminErrorMessage')}:</p>
                            <p className="break-all font-mono text-xs leading-relaxed text-red-800">{selectedJob.error}</p>
                          </div>
                        )}
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('adminTimestamps')}</p>
                        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-2">
                          <div>
                            <span className="text-slate-500">{t('adminCreatedOn')}:</span>
                            <span className="ml-1 text-slate-700">{new Date(selectedJob.createdAtUtc).toLocaleString("pt-PT")}</span>
                          </div>
                          {selectedJob.updatedAtUtc && (
                            <div>
                              <span className="text-slate-500">{t('adminUpdatedOn')}:</span>
                              <span className="ml-1 text-slate-700">{new Date(selectedJob.updatedAtUtc).toLocaleString("pt-PT")}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <DialogFooter className="shrink-0 gap-3 border-t border-slate-200 bg-white px-5 pb-[calc(var(--app-safe-bottom)+1rem)] pt-4 sm:border-t-0 sm:px-0 sm:pb-0 sm:pt-0">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => setIsJobDetailOpen(false)}
                  >
                    {t('close')}
                  </Button>
                  {selectedJob && selectedJob.status === "Failed" && (
                    <Button
                      className="w-full bg-amber-500 hover:bg-amber-600 sm:w-auto"
                      onClick={() => {
                        setIsJobDetailOpen(false)
                        setIsRetryDialogOpen(true)
                      }}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {t('adminRetryJob')}
                    </Button>
                  )}
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Admin;
